import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Modal, Button, Form, Row, Col, Alert, Spinner, Table, InputGroup, Pagination, Badge,
} from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { AuthContext } from '../context/AuthContext';
import microCreditTransactionService from '../services/microCreditTransactionService';
import ReceiptPrintButton from './ReceiptPrintButton';
import { defaultDateRange, validateDateRange } from '../utils/dateFilters';

const badgeVariant = (status) => ({
  PENDING: 'secondary', CONFIRMED: 'info', NOT_CONFIRMED: 'dark',
  IN_PROGRESS: 'primary', OVERDUE: 'warning', CLOSED: 'success',
}[String(status || '').toUpperCase()] || 'secondary');

export default function MicroCreditTransactionModal({ microCreditId, show, onHide }) {
  const { hasRight } = useContext(AuthContext);
  const canView = hasRight('MICRO_CREDIT_VIEW');
  const canDisburse = hasRight('MICRO_CREDIT_DISBURSE');
  const canPayment = hasRight('MICRO_CREDIT_PAYMENT');
  const initialDates = useMemo(() => defaultDateRange(), []);

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { amount: '', note: '' },
  });
  const [account, setAccount] = useState(null);
  const [summary, setSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ruleError, setRuleError] = useState('');
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [dateFrom, setDateFrom] = useState(initialDates.dateFrom);
  const [dateTo, setDateTo] = useState(initialDates.dateTo);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchData = useCallback(async (targetPage = page) => {
    if (!show || !microCreditId || !canView) return;
    const dateError = validateDateRange(dateFrom, dateTo);
    if (dateError) { setRuleError(dateError); setTransactions([]); setTotal(0); return; }
    setLoading(true); setRuleError('');
    try {
      const res = await microCreditTransactionService.listByAccount(microCreditId, {
        page: targetPage, pageSize, search: filter, type: typeFilter, dateFrom, dateTo,
      });
      setAccount(res.account || null);
      setSummary(res.summary || null);
      setTransactions(res.transactions || []);
      setTotal(Number(res.total || 0));
      const pages = Math.max(1, Number(res.totalPages || Math.ceil(Number(res.total || 0) / pageSize)));
      if (targetPage > pages) setPage(pages);
    } catch (e) {
      setRuleError(e?.response?.data?.message || e?.message || 'Erreur chargement transactions.');
    } finally { setLoading(false); }
  }, [canView, dateFrom, dateTo, filter, microCreditId, page, pageSize, show, typeFilter]);

  useEffect(() => { if (show) fetchData(page); }, [show, page, fetchData]);
  useEffect(() => { if (show) setPage(1); }, [filter, typeFilter, dateFrom, dateTo, pageSize, show]);
  useEffect(() => {
    if (!show) {
      reset({ amount: '', note: '' }); setFilter(''); setTypeFilter(''); setPage(1); setTotal(0);
      const dates = defaultDateRange(); setDateFrom(dates.dateFrom); setDateTo(dates.dateTo);
      setRuleError('');
    }
  }, [show, reset]);

  const fmtMoney = (n) => Number(n || 0).toFixed(2);
  const txTypeLabel = (t) => {
    const v = String(t || '').toUpperCase();
    if (v === 'DISBURSEMENT') return 'Décaissement';
    if (v === 'PAYMENT') return 'Paiement';
    return v || '—';
  };

  const paginationItems = useMemo(() => {
    const items = [
      <Pagination.First key="first" disabled={page === 1} onClick={() => setPage(1)} />,
      <Pagination.Prev key="prev" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />,
    ];
    const from = Math.max(1, page - 2); const to = Math.min(totalPages, page + 2);
    if (from > 1) items.push(<Pagination.Ellipsis key="e1" disabled />);
    for (let p = from; p <= to; p += 1) items.push(<Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>);
    if (to < totalPages) items.push(<Pagination.Ellipsis key="e2" disabled />);
    items.push(
      <Pagination.Next key="next" disabled={page === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />,
      <Pagination.Last key="last" disabled={page === totalPages} onClick={() => setPage(totalPages)} />
    );
    return items;
  }, [page, totalPages]);

  const handleDisburse = async () => {
    if (!window.confirm('Confirmer le décaissement du Micro Crédit ?')) return;
    try { setRuleError(''); await microCreditTransactionService.disburse(microCreditId, {}); setPage(1); await fetchData(1); }
    catch (e) { setRuleError(e?.response?.data?.message || e?.message || 'Décaissement impossible.'); }
  };

  const onSubmitPayment = async (vals) => {
    const amount = Number(vals.amount);
    if (!amount || amount <= 0) { setRuleError('Montant invalide.'); return; }
    try {
      setRuleError('');
      await microCreditTransactionService.payment(microCreditId, { amount, note: vals.note || '' });
      reset({ amount: '', note: '' }); setPage(1); await fetchData(1);
    } catch (e) { setRuleError(e?.response?.data?.message || e?.message || 'Paiement impossible.'); }
  };

  const canShowTransactions = !!account && ['CONFIRMED', 'IN_PROGRESS', 'OVERDUE', 'CLOSED'].includes(String(account.status || '').toUpperCase());
  const canDoDisburse = canDisburse && !!account && String(account.status || '').toUpperCase() === 'CONFIRMED' && !summary?.disbursementDone;
  const canDoPayment = canPayment && !!account && !!summary?.disbursementDone &&
    !['CLOSED', 'PENDING', 'NOT_CONFIRMED', 'CONFIRMED'].includes(String(account.status || '').toUpperCase()) && Number(summary?.remainingAmount || 0) > 0;

  const buildReceiptDocument = (t) => {
    const username = t?.agent?.username || '—';
    return {
      title: `Reçu Micro Crédit${t?.codeTransaction ? ` - ${t.codeTransaction}` : ''}`,
      rows: [
        { label: 'Compte', value: account?.accountNumber || '—' },
        { label: 'Client', value: `${account?.client?.firstName || ''} ${account?.client?.lastName || ''}`.trim() || '—' },
        { label: 'Statut compte', value: account?.status || '—' },
        { label: 'ID transaction', value: t?.id || '—' }, { label: 'Code transaction', value: t?.codeTransaction || '—' },
        { label: 'Type', value: txTypeLabel(t?.type) }, { label: 'Montant', value: fmtMoney(t?.amount) },
        { label: 'Total à payer', value: fmtMoney(summary?.totalRepayable) }, { label: 'Déjà payé', value: fmtMoney(t?.collectedAfter) },
        { label: 'Reste à payer', value: fmtMoney(t?.remainingAfter) }, { label: 'Date', value: t?.createdAt ? new Date(t.createdAt).toLocaleString() : '—' },
        { label: 'Devise', value: account?.currency || '—' }, { label: 'Utilisateur', value: username },
      ],
      signatures: { client: 'Signature client', cashier: username },
    };
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton><Modal.Title>Transactions Micro Crédit {account?.accountNumber ? `#${account.accountNumber}` : ''}</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading && !account ? <div className="text-center my-5"><Spinner animation="border" /></div> : <>
          {ruleError && <Alert variant="danger">{ruleError}</Alert>}
          {account && summary && <>
            <Row className="mb-3">
              <Col md={6}><Alert variant="secondary" className="mb-2"><div><strong>Client :</strong> {`${account.client?.firstName || ''} ${account.client?.lastName || ''}`.trim() || '—'}</div><div><strong>Devise :</strong> {account.currency}</div><div><strong>Statut :</strong> <Badge bg={badgeVariant(account.status)}>{account.status}</Badge></div></Alert></Col>
              <Col md={6}><Alert variant={String(account.status || '').toUpperCase() === 'OVERDUE' ? 'warning' : 'info'} className="mb-2"><div><strong>Date création :</strong> {account.startDate ? new Date(account.startDate).toLocaleDateString() : '—'}</div><div><strong>Date fin :</strong> {account.endDate ? new Date(account.endDate).toLocaleDateString() : '—'}</div><div><strong>Durée :</strong> {account.durationDays} jours</div></Alert></Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}><Alert variant="secondary"><div><strong>Total à décaisser :</strong> {fmtMoney(summary.totalDisbursed)}</div><div><strong>Frais :</strong> {fmtMoney(summary.feeAmount)}</div><div><strong>Total à encaisser :</strong> {fmtMoney(summary.totalRepayable)}</div></Alert></Col>
              <Col md={4}><Alert variant="success"><div><strong>Déjà encaissé :</strong> {fmtMoney(summary.totalCollected)}</div><div><strong>Reste à encaisser :</strong> {fmtMoney(summary.remainingAmount)}</div><div><strong>Décaissement effectué :</strong> {summary.disbursementDone ? 'Oui' : 'Non'}</div></Alert></Col>
              <Col md={4}>{canDoDisburse && <Button variant="warning" className="w-100 mb-2" onClick={handleDisburse}>Décaisser {fmtMoney(summary.totalDisbursed)}</Button>}{!summary?.disbursementDone && !canDoDisburse && <Alert variant="light">Le décaissement sera disponible après confirmation.</Alert>}{String(account.status || '').toUpperCase() === 'OVERDUE' && <Alert variant="warning">Ce compte est en retard.</Alert>}</Col>
            </Row>

            {canDoPayment && <Form onSubmit={handleSubmit(onSubmitPayment)} className="mb-4"><h6>Recevoir un paiement</h6><Row className="g-3"><Col md={4}><Form.Group><Form.Label>Montant *</Form.Label><Form.Control type="number" step="0.01" {...register('amount', { required: 'Requis', min: { value: 0.01, message: '> 0' } })} />{errors.amount && <Form.Text className="text-danger">{errors.amount.message}</Form.Text>}</Form.Group></Col><Col md={6}><Form.Group><Form.Label>Note</Form.Label><Form.Control {...register('note')} placeholder="Optionnel" /></Form.Group></Col><Col md={2} className="d-flex align-items-end"><Button type="submit" className="w-100">Encaisser</Button></Col></Row></Form>}

            {canView && canShowTransactions && <>
              <h6>Historique des transactions</h6>
              <Row className="g-2 align-items-end mb-3">
                <Col md={2}><Form.Label>Date début</Form.Label><Form.Control type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></Col>
                <Col md={2}><Form.Label>Date fin</Form.Label><Form.Control type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></Col>
                <Col md={4}><Form.Label>Recherche</Form.Label><InputGroup><Form.Control placeholder="Type, note, code..." value={filter} onChange={(e) => setFilter(e.target.value)} /><Button variant="outline-secondary" onClick={() => setFilter('')}>×</Button></InputGroup></Col>
                <Col md={2}><Form.Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">Tous types</option><option value="DISBURSEMENT">Décaissement</option><option value="PAYMENT">Paiement</option></Form.Select></Col>
                <Col md={2}><Form.Select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}><option value={10}>10 / page</option><option value={25}>25 / page</option><option value={50}>50 / page</option></Form.Select></Col>
              </Row>
              <Table hover responsive><thead><tr><th>ID</th><th>Type</th><th>Montant</th><th>Déjà encaissé après</th><th>Reste après</th><th>Date</th><th>Code</th><th>Actions</th></tr></thead><tbody>
                {transactions.map((t) => <tr key={t.id}><td>{t.id}</td><td>{txTypeLabel(t.type)}</td><td>{fmtMoney(t.amount)}</td><td>{fmtMoney(t.collectedAfter)}</td><td>{fmtMoney(t.remainingAfter)}</td><td>{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</td><td>{t.codeTransaction}</td><td><ReceiptPrintButton getDocument={() => buildReceiptDocument(t)} /></td></tr>)}
                {transactions.length === 0 && <tr><td colSpan={8} className="text-center text-muted">Aucune transaction pour cette période.</td></tr>}
              </tbody></Table>
              <div className="d-flex justify-content-between align-items-center"><span className="text-muted">Résultats : {total} • Page {page}/{totalPages}</span><Pagination className="mb-0">{paginationItems}</Pagination></div>
            </>}
          </>}
        </>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Fermer</Button></Modal.Footer>
    </Modal>
  );
}
